<template>
    <div>
        <portal to="page-header">{{ $t('Players') }}</portal>

        <portal to="actions">
            <div class="flex">
                <btn-link :href="route('terminal.scan')" class="mr-3">
                    <span class="hidden sm:inline">{{ $t('Scan Terminal') }}</span>
                    <span class="sm:hidden md:hidden lg:hidden xl:hidden">{{ $t('Scan') }}</span>
                </btn-link>
                <btn-link :href="route('players.create')">
                    <span class="hidden sm:inline">{{ $t('Create Player') }}</span>
                    <span class="sm:hidden md:hidden lg:hidden xl:hidden">{{ $t('Create') }}</span>
                </btn-link>
            </div>
        </portal>

        <div class="flex flex-wrap">
            <div class="sm:px-1 md:px-3 lg:px-3 xl:px-3 w-full">
                <div class="flex justify-end">
                    <search-filter v-model="form.search" class="w-full max-w-md mb-6" @reset="reset">
                        <select v-model="form.filter" class="w-full bg-white rounded-l border-r filter-select">
                            <option :value="null">{{ $t('All') }}</option>
                            <option value="active">{{ $t('Active') }}</option>
                            <option value="inactive">{{ $t('Inactive') }}</option>
                            <option value="wd">{{ $t('Withdrawal') }}</option>
                        </select>
                    </search-filter>
                </div>
                <div v-if="players.data.length > 0" class="w-full overflow-hidden shadow-xs">
                    <div class="w-full overflow-x-auto rounded-t">
                        <table class="w-full whitespace-no-wrap">
                            <thead>
                            <tr class="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase border-b dark:border-gray-700 bg-gray-50 dark:text-gray-400 dark:bg-gray-800">
                                <th class="px-4 py-3">{{ $t('Username') }}</th>
                                <th class="px-4 py-3 text-center">{{ $t('Type') }}</th>
                                <th class="px-4 py-3 text-right">{{ $t('Balance') }}</th>
                                <th class="px-4 py-3 text-right">{{ $t('Created At') }}</th>
                                <th class="px-4 py-3 text-right">{{ $t('Actions') }}</th>
                            </tr>
                            </thead>
                            <tbody class="bg-gray-200 divide-y dark:divide-gray-700 dark:bg-gray-800">
                            <tr v-for="player in players.data" :key="player.id" class="text-gray-700 dark:text-gray-400">
                                <td class="px-4 py-3">
                                    <inertia-link :href="route('players.show', player.id)">
                                        <span
                                            class="px-2 py-1 font-semibold leading-tight"
                                            :class="{
                                                'text-green-700 bg-green-100': player.active,
                                                'text-red-700 bg-red-100': !player.active
                                            }"
                                        >

                                          {{ player.username }}
                                        </span>
                                    </inertia-link>
                                </td>
                                <td class="text-center">
                                    <font-awesome-icon :icon="playerIcon(player.type)" />
                                </td>
                                <td class="px-4 py-3 text-right">
                                    <span v-if="player.main_wallet">
                                        {{ showFormattedAmount(player.main_wallet.amount, player.main_wallet.currency.code) }}
                                    </span>
                                </td>
                                <td class="px-4 py-3 text-right">
                                    {{ player.created_at | datetime }}
                                </td>
                                <td class="px-4 py-3 text-right">
                                    <div class="flex justify-end space-x-4">
                                        <btn-link class="ml-4" :aria-label="$t('Details')" :title="$t('Details')" :href="route('players.show', player.id)">
                                            {{ $t('Details') }}
                                        </btn-link>
                                    </div>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="flex justify-between px-4 py-3 text-md font-semibold tracking-wide text-gray-500 rounded-b uppercase border-t dark:border-gray-700 bg-gray-50 sm:grid-cols-9 dark:text-gray-400 dark:bg-gray-800">
                <span class="flex items-center col-span-3">
                  {{ players.from }} - {{ players.to }} {{ $t('of') }} {{ players.total }}
                </span>
                        <span class="col-span-2"></span>
                        <pagination :links="players.links" />
                    </div>
                </div>
                <div v-else class="flex flex-wrap justify-center text-2xl text-white">
                    {{ $t('No Entries') }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { debounce, pickBy, mapValues } from 'lodash';
import Pagination from '../../Components/Pagination';
import SearchFilter from '../../Components/SearchFilter';
import PageHeader from '../../Components/PageHeader';
import BtnLink from '../../Components/BtnLink';

export default {
    name: "Index",
    components: {BtnLink, PageHeader, Pagination, SearchFilter},
    props: {
        players: Object,
        filters: Object,
    },
    metaInfo() {
        return {
            title: this.title
        }
    },
    computed: {
        title () {
            return this.$t('Players');
        }
    },
    data() {
        return {
            form: {
                search: this.filters.search,
                filter: this.filters.filter,
            },
        }
    },
    watch: {
        form: {
            handler: debounce(function() {
                let query = pickBy(this.form);
                this.$inertia.get(
                    this.route(
                        'players.index',
                        Object.keys(query).length ? query : { remember: 'forget' })
                );
            }, 1000),
            deep: true,
        },
    },
    methods: {
        reset() {
            this.form = mapValues(this.form, () => null);
        },

        playerIcon(type) {
            return type === 'terminal' ? 'desktop' : 'user';
        },

        showFormattedAmount(amount, currencyCode) {
            return this.$formatCurrency(amount, currencyCode);
        }
    }
}
</script>

<style lang="scss" scoped>
    tr:nth-child(even) {
        background-color: #f4f5f7;
    }
    .filter-select {
        max-width: 120px;
    }
</style>
